import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Security as SecurityIcon,
  IntegrationInstructions as IntegrationIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { settingsAPI } from '../../../services/leadGenAPI';

const LeadGenSettings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    account: {
      companyName: 'Novaura CRM',
      industry: 'Technology',
      timezone: 'America/New_York',
      currency: 'USD',
      language: 'en',
      leadQualityThreshold: 70,
      autoApproveLeads: false,
      maxLeadsPerDay: 1000,
      budgetLimit: 5000,
    },
    compliance: {
      gdprEnabled: true,
      ccpaEnabled: true,
      canSpamEnabled: true,
      doubleOptIn: true,
      dataRetentionDays: 730,
      allowInternationalLeads: true,
      restrictedCountries: ['CN', 'RU', 'IR'],
      ageRestriction: 18,
      consentRequired: true,
    },
    integrations: {
      crmSync: {
        enabled: true,
        provider: 'salesforce',
        syncInterval: 15,
        autoImport: true,
      },
      emailProvider: {
        enabled: true,
        provider: 'mailchimp',
        apiKey: '********',
        listId: 'main-list',
      },
      analytics: {
        enabled: true,
        provider: 'google',
        trackingId: 'GA-123456789',
      },
    },
  });

  const [loading, setLoading] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const [accountSettings, complianceSettings, integrationSettings] = await Promise.all([
        settingsAPI.getAccountSettings(),
        settingsAPI.getComplianceRules(),
        settingsAPI.getIntegrationSettings(),
      ]);
      
      setSettings({
        account: accountSettings,
        compliance: complianceSettings,
        integrations: integrationSettings,
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await Promise.all([
        settingsAPI.updateAccountSettings(settings.account),
        settingsAPI.updateComplianceRules(settings.compliance),
        settingsAPI.updateIntegrationSettings(settings.integrations),
      ]);
      setSaveDialogOpen(false);
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderAccountSettings = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom color="text.primary">
          Account Configuration
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Company Name"
              value={settings.account.companyName}
              onChange={(e) => handleSettingChange('account', 'companyName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Industry</InputLabel>
              <Select
                value={settings.account.industry}
                label="Industry"
                onChange={(e) => handleSettingChange('account', 'industry', e.target.value)}
              >
                <MenuItem value="Technology">Technology</MenuItem>
                <MenuItem value="Healthcare">Healthcare</MenuItem>
                <MenuItem value="Finance">Finance</MenuItem>
                <MenuItem value="Education">Education</MenuItem>
                <MenuItem value="Retail">Retail</MenuItem>
                <MenuItem value="Manufacturing">Manufacturing</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Timezone</InputLabel>
              <Select
                value={settings.account.timezone}
                label="Timezone"
                onChange={(e) => handleSettingChange('account', 'timezone', e.target.value)}
              >
                <MenuItem value="America/New_York">Eastern Time</MenuItem>
                <MenuItem value="America/Chicago">Central Time</MenuItem>
                <MenuItem value="America/Denver">Mountain Time</MenuItem>
                <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                <MenuItem value="Europe/London">London</MenuItem>
                <MenuItem value="Europe/Paris">Paris</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select
                value={settings.account.currency}
                label="Currency"
                onChange={(e) => handleSettingChange('account', 'currency', e.target.value)}
              >
                <MenuItem value="USD">USD ($)</MenuItem>
                <MenuItem value="EUR">EUR (€)</MenuItem>
                <MenuItem value="GBP">GBP (£)</MenuItem>
                <MenuItem value="CAD">CAD (C$)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Lead Quality Threshold (%)"
              type="number"
              value={settings.account.leadQualityThreshold}
              onChange={(e) => handleSettingChange('account', 'leadQualityThreshold', parseInt(e.target.value))}
              inputProps={{ min: 0, max: 100 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Max Leads Per Day"
              type="number"
              value={settings.account.maxLeadsPerDay}
              onChange={(e) => handleSettingChange('account', 'maxLeadsPerDay', parseInt(e.target.value))}
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Daily Budget Limit"
              type="number"
              value={settings.account.budgetLimit}
              onChange={(e) => handleSettingChange('account', 'budgetLimit', parseFloat(e.target.value))}
              InputProps={{
                startAdornment: '$',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.account.autoApproveLeads}
                  onChange={(e) => handleSettingChange('account', 'autoApproveLeads', e.target.checked)}
                />
              }
              label="Auto-approve leads above quality threshold"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderComplianceSettings = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom color="text.primary">
          Compliance Rules
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.compliance.gdprEnabled}
                  onChange={(e) => handleSettingChange('compliance', 'gdprEnabled', e.target.checked)}
                />
              }
              label="GDPR Compliance"
            />
            <Typography variant="caption" display="block" color="textSecondary">
              Enable GDPR compliance for EU leads
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.compliance.ccpaEnabled}
                  onChange={(e) => handleSettingChange('compliance', 'ccpaEnabled', e.target.checked)}
                />
              }
              label="CCPA Compliance"
            />
            <Typography variant="caption" display="block" color="textSecondary">
              Enable CCPA compliance for California leads
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.compliance.canSpamEnabled}
                  onChange={(e) => handleSettingChange('compliance', 'canSpamEnabled', e.target.checked)}
                />
              }
              label="CAN-SPAM Compliance"
            />
            <Typography variant="caption" display="block" color="textSecondary">
              Enable CAN-SPAM compliance for email marketing
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.compliance.doubleOptIn}
                  onChange={(e) => handleSettingChange('compliance', 'doubleOptIn', e.target.checked)}
                />
              }
              label="Double Opt-in"
            />
            <Typography variant="caption" display="block" color="textSecondary">
              Require email confirmation for leads
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Data Retention (days)"
              type="number"
              value={settings.compliance.dataRetentionDays}
              onChange={(e) => handleSettingChange('compliance', 'dataRetentionDays', parseInt(e.target.value))}
              inputProps={{ min: 30, max: 3650 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Minimum Age"
              type="number"
              value={settings.compliance.ageRestriction}
              onChange={(e) => handleSettingChange('compliance', 'ageRestriction', parseInt(e.target.value))}
              inputProps={{ min: 13, max: 100 }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.compliance.allowInternationalLeads}
                  onChange={(e) => handleSettingChange('compliance', 'allowInternationalLeads', e.target.checked)}
                />
              }
              label="Allow International Leads"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Restricted Countries
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {settings.compliance.restrictedCountries.map((country, index) => (
                <Chip
                  key={country}
                  label={country}
                  onDelete={() => {
                    const newCountries = settings.compliance.restrictedCountries.filter(c => c !== country);
                    handleSettingChange('compliance', 'restrictedCountries', newCountries);
                  }}
                  color="error"
                  variant="outlined"
                />
              ))}
              <Button size="small" startIcon={<AddIcon />}>
                Add Country
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderIntegrationSettings = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom color="text.primary">
          Integration Settings
        </Typography>
        
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={2}>
              <IntegrationIcon />
              <Typography variant="subtitle1" color="text.primary">CRM Integration</Typography>
              <Chip
                label={settings.integrations.crmSync.enabled ? 'Connected' : 'Disconnected'}
                color={settings.integrations.crmSync.enabled ? 'success' : 'default'}
                size="small"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.integrations.crmSync.enabled}
                      onChange={(e) => handleSettingChange('integrations', 'crmSync', {
                        ...settings.integrations.crmSync,
                        enabled: e.target.checked
                      })}
                    />
                  }
                  label="Enable CRM Sync"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>CRM Provider</InputLabel>
                  <Select
                    value={settings.integrations.crmSync.provider}
                    label="CRM Provider"
                    onChange={(e) => handleSettingChange('integrations', 'crmSync', {
                      ...settings.integrations.crmSync,
                      provider: e.target.value
                    })}
                  >
                    <MenuItem value="salesforce">Salesforce</MenuItem>
                    <MenuItem value="hubspot">HubSpot</MenuItem>
                    <MenuItem value="pipedrive">Pipedrive</MenuItem>
                    <MenuItem value="zoho">Zoho CRM</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Sync Interval (minutes)"
                  type="number"
                  value={settings.integrations.crmSync.syncInterval}
                  onChange={(e) => handleSettingChange('integrations', 'crmSync', {
                    ...settings.integrations.crmSync,
                    syncInterval: parseInt(e.target.value)
                  })}
                  inputProps={{ min: 5, max: 1440 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.integrations.crmSync.autoImport}
                      onChange={(e) => handleSettingChange('integrations', 'crmSync', {
                        ...settings.integrations.crmSync,
                        autoImport: e.target.checked
                      })}
                    />
                  }
                  label="Auto-import leads"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={2}>
              <IntegrationIcon />
              <Typography variant="subtitle1" color="text.primary">Email Provider</Typography>
              <Chip
                label={settings.integrations.emailProvider.enabled ? 'Connected' : 'Disconnected'}
                color={settings.integrations.emailProvider.enabled ? 'success' : 'default'}
                size="small"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.integrations.emailProvider.enabled}
                      onChange={(e) => handleSettingChange('integrations', 'emailProvider', {
                        ...settings.integrations.emailProvider,
                        enabled: e.target.checked
                      })}
                    />
                  }
                  label="Enable Email Integration"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Email Provider</InputLabel>
                  <Select
                    value={settings.integrations.emailProvider.provider}
                    label="Email Provider"
                    onChange={(e) => handleSettingChange('integrations', 'emailProvider', {
                      ...settings.integrations.emailProvider,
                      provider: e.target.value
                    })}
                  >
                    <MenuItem value="mailchimp">Mailchimp</MenuItem>
                    <MenuItem value="constant_contact">Constant Contact</MenuItem>
                    <MenuItem value="sendgrid">SendGrid</MenuItem>
                    <MenuItem value="klaviyo">Klaviyo</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="API Key"
                  type="password"
                  value={settings.integrations.emailProvider.apiKey}
                  onChange={(e) => handleSettingChange('integrations', 'emailProvider', {
                    ...settings.integrations.emailProvider,
                    apiKey: e.target.value
                  })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="List ID"
                  value={settings.integrations.emailProvider.listId}
                  onChange={(e) => handleSettingChange('integrations', 'emailProvider', {
                    ...settings.integrations.emailProvider,
                    listId: e.target.value
                  })}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={2}>
              <IntegrationIcon />
              <Typography variant="subtitle1" color="text.primary">Analytics</Typography>
              <Chip
                label={settings.integrations.analytics.enabled ? 'Connected' : 'Disconnected'}
                color={settings.integrations.analytics.enabled ? 'success' : 'default'}
                size="small"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.integrations.analytics.enabled}
                      onChange={(e) => handleSettingChange('integrations', 'analytics', {
                        ...settings.integrations.analytics,
                        enabled: e.target.checked
                      })}
                    />
                  }
                  label="Enable Analytics"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Analytics Provider</InputLabel>
                  <Select
                    value={settings.integrations.analytics.provider}
                    label="Analytics Provider"
                    onChange={(e) => handleSettingChange('integrations', 'analytics', {
                      ...settings.integrations.analytics,
                      provider: e.target.value
                    })}
                  >
                    <MenuItem value="google">Google Analytics</MenuItem>
                    <MenuItem value="mixpanel">Mixpanel</MenuItem>
                    <MenuItem value="amplitude">Amplitude</MenuItem>
                    <MenuItem value="hotjar">Hotjar</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Tracking ID"
                  value={settings.integrations.analytics.trackingId}
                  onChange={(e) => handleSettingChange('integrations', 'analytics', {
                    ...settings.integrations.analytics,
                    trackingId: e.target.value
                  })}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" color="text.primary">
          Lead Generation Settings
        </Typography>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={() => setSaveDialogOpen(true)}
          disabled={loading}
        >
          Save Settings
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          {renderAccountSettings()}
        </Grid>
        <Grid item xs={12}>
          {renderComplianceSettings()}
        </Grid>
        <Grid item xs={12}>
          {renderIntegrationSettings()}
        </Grid>
      </Grid>

      {/* Save Confirmation Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>Save Settings</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to save these settings? This will update your lead generation configuration.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadGenSettings; 